import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CModal, CModalBody } from "@coreui/react-pro";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../scss/_whatsapp_messages.scss";
import * as MarketsmsService from "../../services/MarketsmsService";
import { ConversationsItems } from "../../models/Conversations";
import { ConversationMessagesItems } from "../../models/ConversationMessages";
import { db } from "../../firebase/FirebaseConfig";
import { getCurrentAccountId, getDefaultPhoneNumber, showToastTC } from "../../Utils/BaseApp";
import { format } from "date-fns";
import { ConversationList } from "./conversations/ConversationList";
import { MessageBubble } from "./conversations/MessageBubble";
import { Composer } from "./conversations/Composer";
import { avatarColor, dayKey, displayName, fileKind, formatDayLabel, getInitials, MediaKind, parseDate } from "./conversations/utils";

const NEAR_BOTTOM_PX = 120;

export const WhatsappConversations: React.FC = () => {
  const navigate = useNavigate();
  const [phoneNumberId] = useState<number | null>(() => getDefaultPhoneNumber());

  const [conversations, setConversations] = useState<ConversationsItems[] | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ConversationMessagesItems[] | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const [mediaModal, setMediaModal] = useState<{ url: string; kind: MediaKind } | null>(null);

  const selectedIdRef = useRef<number | null>(null);
  const messagesRequestRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const forceScrollRef = useRef(false);
  // Archivos de envíos fallidos, para poder reintentar
  const pendingFilesRef = useRef<Map<number, { file: File | null; isVoice: boolean }>>(new Map());
  // URLs locales creadas para mensajes optimistas (se liberan al desmontar)
  const localUrlsRef = useRef<string[]>([]);

  const selected = useMemo(
    () => conversations?.find((c) => c.id === selectedId) ?? null,
    [conversations, selectedId]
  );

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    const urls = localUrlsRef.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  // ---------- Carga de datos ----------

  const loadConversations = useCallback(async () => {
    if (phoneNumberId == null) return;
    setLoadingConversations(true);
    try {
      const account_id = await getCurrentAccountId();
      const response = await MarketsmsService.getConversations(phoneNumberId, account_id);
      if (response?.status === 200) {
        const items = response.data.items ?? [];
        // El chat abierto no debe volver a marcarse como no leído
        setConversations(items.map((c) => (c.id === selectedIdRef.current ? { ...c, unread: 0 } : c)));
        setConversationsError(null);
      } else {
        setConversationsError("No se pudieron cargar las conversaciones.");
      }
    } catch {
      setConversationsError("Error de conexión al cargar las conversaciones.");
    } finally {
      setLoadingConversations(false);
    }
  }, [phoneNumberId]);

  const loadMessages = useCallback(async (conversationId: number, silent = false) => {
    if (phoneNumberId == null) return;
    const requestId = ++messagesRequestRef.current;
    if (!silent) {
      setLoadingMessages(true);
      setMessagesError(null);
    }
    try {
      const account_id = await getCurrentAccountId();
      const response = await MarketsmsService.getConversationMessages(phoneNumberId, conversationId, account_id);
      // Ignorar respuestas viejas (el usuario cambió de chat mientras cargaba)
      if (requestId !== messagesRequestRef.current || conversationId !== selectedIdRef.current) return;

      if (response?.status === 200) {
        const items = response.data.items ?? [];
        setMessages((previous) => {
          // Conservar mensajes optimistas que aún no confirma el servidor
          const pending = (previous ?? []).filter((m) => m.local_status);
          return [...items, ...pending];
        });
        const last = items[items.length - 1];
        if (last) {
          setConversations((previous) =>
            previous?.map((chat) =>
              chat.id === conversationId
                ? {
                    ...chat,
                    last_message: last.body ?? "",
                    media_url: last.media_url,
                    message_date: last.created_at,
                    type: last.type,
                    message_status: last.whatsapp_statuses_id,
                    unread: 0,
                  }
                : chat
            ) ?? previous
          );
        }
      } else if (!silent) {
        setMessagesError("No se pudieron cargar los mensajes.");
      }
    } catch {
      if (!silent && requestId === messagesRequestRef.current) setMessagesError("Error de conexión al cargar los mensajes.");
    } finally {
      if (!silent && requestId === messagesRequestRef.current) setLoadingMessages(false);
    }
  }, [phoneNumberId]);

  // Carga inicial + escucha de Firestore para la lista (un solo listener por número)
  useEffect(() => {
    if (phoneNumberId == null) return;
    loadConversations();

    let debounce: ReturnType<typeof setTimeout> | null = null;
    let first = true;
    const q = query(collection(db, "chats"), where("phone_number_id", "==", phoneNumberId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (first) {
        first = false; // la primera emisión es el estado actual; ya se cargó arriba
        return;
      }
      const onlyCurrentChat = snapshot.docChanges().length > 0 && snapshot.docChanges().every(
        (change) => change.doc.data().conversation_id === selectedIdRef.current
      );
      if (onlyCurrentChat) return; // el listener del chat abierto se encarga
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(loadConversations, 300);
    });

    return () => {
      if (debounce) clearTimeout(debounce);
      unsubscribe();
    };
  }, [phoneNumberId, loadConversations]);

  // Mensajes del chat abierto + escucha en tiempo real
  useEffect(() => {
    if (selectedId == null || phoneNumberId == null) return;
    setMessages(null);
    forceScrollRef.current = true;
    loadMessages(selectedId);

    let first = true;
    const q = query(collection(db, "conversations"), where("conversation_id", "==", selectedId));
    const unsubscribe = onSnapshot(q, () => {
      if (first) {
        first = false;
        return;
      }
      loadMessages(selectedId, true);
    });
    return () => unsubscribe();
  }, [selectedId, phoneNumberId, loadMessages]);

  // ---------- Scroll ----------

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  useLayoutEffect(() => {
    if (!messages) return;
    if (forceScrollRef.current || nearBottomRef.current) {
      scrollToBottom(!forceScrollRef.current);
      forceScrollRef.current = false;
      nearBottomRef.current = true;
    }
  }, [messages, scrollToBottom]);

  const handleMediaLoad = useCallback(() => {
    if (nearBottomRef.current) scrollToBottom();
  }, [scrollToBottom]);

  // ---------- Acciones ----------

  const selectConversation = (conversation: ConversationsItems) => {
    if (conversation.id === selectedId) return;
    setSelectedId(conversation.id);
    setConversations((previous) => previous?.map((c) => (c.id === conversation.id ? { ...c, unread: 0 } : c)) ?? previous);
  };

  const deliver = async (conversationId: number, tempId: number, text: string, file: File | null, isVoice: boolean) => {
    try {
      const account_id = await getCurrentAccountId();
      const response = await MarketsmsService.sendIndividualMessage(conversationId, account_id, text, file, isVoice);
      if (response?.status !== 200) throw new Error((response as any)?.data?.error ?? response?.message ?? "Error al enviar");
      pendingFilesRef.current.delete(tempId);
      if (selectedIdRef.current === conversationId) {
        // Se deja visible (sin estado local) hasta que la recarga traiga el mensaje real: evita parpadeo
        setMessages((previous) => previous?.map((m) => (m.id === tempId ? { ...m, local_status: undefined } : m)) ?? previous);
        loadMessages(conversationId, true);
      }
      loadConversations();
    } catch (error: any) {
      setMessages((previous) => previous?.map((m) => (m.id === tempId ? { ...m, local_status: "failed" } : m)) ?? previous);
      const reason = typeof error?.message === "string" && error.message !== "Error al enviar" ? ` Motivo: ${error.message}` : "";
      showToastTC(`No se pudo enviar el mensaje.${reason} Puedes reintentarlo.`, 6000, "error");
    }
  };

  const handleSend = (text: string, file: File | null, isVoice: boolean) => {
    if (selectedId == null) return;
    const tempId = -Date.now();
    let localUrl: string | undefined;
    if (file) {
      localUrl = URL.createObjectURL(file);
      localUrlsRef.current.push(localUrl);
    }
    const now = format(new Date(), "yyyy-MM-dd HH:mm:ss");
    const optimistic: ConversationMessagesItems = {
      id: tempId,
      type: "outbound",
      body: text,
      variables: "",
      media_url: "",
      whatsapp_templates_id: 0,
      service_types_id: 0,
      whatsapp_statuses_id: 1,
      campaigns_id: null,
      template: null,
      created_at: now,
      updated_at: now,
      local_status: "sending",
      local_media_url: localUrl,
      local_media_kind: file ? fileKind(file) : undefined,
      local_file_name: file?.name,
    };
    pendingFilesRef.current.set(tempId, { file, isVoice });
    nearBottomRef.current = true;
    setMessages((previous) => [...(previous ?? []), optimistic]);
    deliver(selectedId, tempId, text, file, isVoice);
  };

  const handleRetry = (message: ConversationMessagesItems) => {
    if (selectedId == null) return;
    const pending = pendingFilesRef.current.get(message.id) ?? { file: null, isVoice: false };
    setMessages((previous) => previous?.map((m) => (m.id === message.id ? { ...m, local_status: "sending" } : m)) ?? previous);
    deliver(selectedId, message.id, message.body ?? "", pending.file, pending.isVoice);
  };

  // ---------- Render ----------

  const renderedMessages = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    let lastDay = "";
    (messages ?? []).forEach((message) => {
      const key = dayKey(message.created_at);
      if (key && key !== lastDay) {
        lastDay = key;
        const date = parseDate(message.created_at);
        nodes.push(
          <div key={`day-${key}`} className="wa-day"><span>{date ? formatDayLabel(date) : ""}</span></div>
        );
      }
      nodes.push(
        <MessageBubble
          key={message.id}
          message={message}
          onOpenMedia={(url, kind) => setMediaModal({ url, kind })}
          onMediaLoad={handleMediaLoad}
          onRetry={handleRetry}
        />
      );
    });
    return nodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, handleMediaLoad, selectedId]);

  if (phoneNumberId == null) {
    return (
      <div className="wa-inbox wa-inbox--empty">
        <div className="wa-state">
          <i className="bi bi-whatsapp fs-1 d-block mb-3 text-success" />
          <h5>No tienes un número de WhatsApp seleccionado</h5>
          <p className="text-body-secondary">Elige un número por defecto para ver sus conversaciones.</p>
          <button type="button" className="btn btn-success" onClick={() => navigate("/whatsapp/numbers")}>Ir a mis números</button>
        </div>
      </div>
    );
  }

  const selectedName = selected ? displayName(selected.first_name, selected.last_name, selected.number) : "";

  return (
    <>
      <div className={`wa-inbox ${selectedId != null ? "wa-inbox--chat-open" : ""}`}>
        <aside className="wa-sidebar" aria-label="Conversaciones">
          <div className="wa-sidebar-title">
            <i className="bi bi-whatsapp text-success me-2" />Conversaciones
            <button type="button" className="wa-icon-btn ms-auto" onClick={loadConversations} aria-label="Actualizar" title="Actualizar">
              <i className={`bi bi-arrow-clockwise ${loadingConversations ? "wa-spin" : ""}`} />
            </button>
          </div>
          <ConversationList
            conversations={conversations}
            loading={loadingConversations}
            error={conversationsError}
            selectedId={selectedId}
            onSelect={selectConversation}
            onRetry={loadConversations}
          />
        </aside>

        <section className="wa-chat" aria-label="Chat">
          {selectedId != null && selected ? (
            <>
              <header className="wa-chat-header">
                <button type="button" className="wa-icon-btn d-md-none" onClick={() => setSelectedId(null)} aria-label="Volver a la lista">
                  <i className="bi bi-arrow-left" />
                </button>
                <span className="wa-avatar" style={{ background: avatarColor(selected.contact_id ?? selected.number) }}>
                  {getInitials(selected.first_name || selected.number)}
                </span>
                <div className="min-w-0">
                  <div className="fw-semibold text-truncate">{selectedName}</div>
                  {(selected.first_name || selected.last_name) && <div className="small text-body-secondary">+{selected.number}</div>}
                </div>
              </header>

              <div className="wa-messages" ref={scrollRef} onScroll={handleScroll}>
                {loadingMessages && !messages && (
                  <div className="wa-state"><span className="spinner-border spinner-border-sm me-2" />Cargando mensajes…</div>
                )}
                {messagesError && (
                  <div className="wa-state text-danger">
                    {messagesError}
                    <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => loadMessages(selectedId)}>Reintentar</button>
                  </div>
                )}
                {messages && messages.length === 0 && (
                  <div className="wa-state">No hay mensajes en esta conversación.</div>
                )}
                {renderedMessages}
              </div>

              <Composer onSend={handleSend} disabled={!messages} />
            </>
          ) : (
            <div className="wa-placeholder">
              <i className="bi bi-chat-dots" />
              <h5>Selecciona una conversación</h5>
              <p>Los mensajes que respondas desde la app WhatsApp Business también aparecen aquí.</p>
            </div>
          )}
        </section>
      </div>

      <CModal visible={!!mediaModal} size="xl" alignment="center" onClose={() => setMediaModal(null)}>
        <CModalBody className="p-0 bg-black text-center">
          {mediaModal?.kind === "image" && <img src={mediaModal.url} alt="Imagen ampliada" className="wa-modal-media" />}
          {mediaModal?.kind === "video" && <video src={mediaModal.url} controls autoPlay className="wa-modal-media" />}
        </CModalBody>
      </CModal>
    </>
  );
};
